// src/components/CountryRedirect.js
import React, { useEffect } from 'react';
import { navigate } from 'gatsby';
import {
  countries,
  defaultCountry,
  getDefaultLangForCountry,
  isLangSupportedForCountry
} from '../config/countries';
import { detectCountry } from 'helpers/utils';

// Check if we're in browser environment
const isBrowser = typeof window !== 'undefined';

const CountryRedirect = ({ children }) => {
  useEffect(() => {
    // Only run redirect logic in browser
    if (!isBrowser) return;

    const path = window.location.pathname;

    // Everything after the path must survive the redirect below. An ad pointing
    // at the bare root (https://shipper.market/?utm_source=meta&fbclid=…) lands
    // here, gets sent to /tn/fr/, and before this was added every UTM param,
    // fbclid/gclid and ?a=/?ref= was silently dropped from the URL — so a
    // campaign signup was attribution-blind before the visitor ever saw a form.
    const query = window.location.search + window.location.hash;

    // Skip redirect for utility pages (e.g. /join/workshop) and the legacy
    // /careers shim (src/pages/careers.js), which handles its own redirect to
    // the canonical /{country}/{lang}/careers/ page preserving query params.
    if (path.startsWith('/join/') || path === '/careers' || path.startsWith('/careers/')) return;

    const pathSegments = path.split('/').filter(Boolean);

    // If the URL already has a valid country and language, do nothing.
    if (pathSegments.length >= 2) {
      const [country, lang] = pathSegments;
      if (countries[country] && isLangSupportedForCountry(country, lang)) {
        localStorage.setItem('country', country);
        localStorage.setItem('lang', lang);
        return;
      }
    }

    // Try to get previously stored preferences.
    const storedCountry = localStorage.getItem('country');
    const storedLang = localStorage.getItem('lang');

    if (storedCountry && countries[storedCountry]) {
      const targetLang =
        storedLang && isLangSupportedForCountry(storedCountry, storedLang)
          ? storedLang
          : getDefaultLangForCountry(storedCountry);
      navigate(`/${storedCountry}/${targetLang}/${query}`, { replace: true });
      return;
    }

    // Otherwise, detect the country using an IP lookup.
    (async () => {
      const detectedCountry = await detectCountry();
      const countryCode =
        detectedCountry && countries[detectedCountry]
          ? detectedCountry
          : defaultCountry;
      const lang = getDefaultLangForCountry(countryCode);
      // Save the detected values for future visits.
      localStorage.setItem('country', countryCode);
      localStorage.setItem('lang', lang);
      navigate(`/${countryCode}/${lang}/${query}`, { replace: true });
    })();
  }, []);

  // Always render children for both SSR and client
  return <>{children}</>;
};

export default CountryRedirect;
